import React, { useState, useEffect, useRef } from "react";
import Sidebar from "./Sidebar";
import axios from "axios";
import Swal from "sweetalert2";
import "./dashboard.css";
import "./soloStudent.css";

// const API_BASE_URL = "https://mhsdev.online/MHS-WEB/api"; // Your PHP backend
const API_BASE_URL = "https://mhsdev.online/MHS-WEB/api"; // Your PHP backend
const AddStudentForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    fatherName: "",
    mobile: "",
    bForm: "",
    address: "",
    gender: "",
    class: "",
    section: "",
    studentId: "",
  });

  const [sections, setSections] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [charCount, setCharCount] = useState(0);
  const [isGeneratingId, setIsGeneratingId] = useState(false);

  const classes = [
    "Play Group",
    "Nursery",
    "Prep",
    "Class 1",
    "Class 2",
    "Class 3",
    "Class 4",
    "Class 5",
    "Class 6",
    "Class 7",
    "Class 8",
    "Class Pre 9",
    "Class 9",
    "Class 10",
  ];

  const classShortForms = {
    "Play Group": "PG",
    Nursery: "Nur",
    Prep: "Prep",
    "Class 1": "1",
    "Class 2": "2",
    "Class 3": "3",
    "Class 4": "4",
    "Class 5": "5",
    "Class 6": "6",
    "Class 7": "7",
    "Class 8": "8",
    "Class Pre 9": "P9",
    "Class 9": "9",
    "Class 10": "10",
  };

  // Formatting helper for Mobile (XXXX-XXXXXXX)
  const formatMobile = (val) => {
    const cleaned = val.replace(/\D/g, ''); // Allow only digits
    if (cleaned.length <= 4) return cleaned;
    if (cleaned.length <= 11) return cleaned.slice(0, 4) + '-' + cleaned.slice(4, 11);
    return cleaned.slice(0, 4) + '-' + cleaned.slice(4, 11);
  };

  // Formatting helper for CNIC (XXXXX-XXXXXXX-X)
  const formatCNIC = (val) => {
    const cleaned = val.replace(/\D/g, ''); // Allow only digits
    if (cleaned.length <= 5) return cleaned;
    if (cleaned.length <= 12) return cleaned.slice(0, 5) + '-' + cleaned.slice(5, 12);
    return cleaned.slice(0, 5) + '-' + cleaned.slice(5, 12) + '-' + cleaned.slice(12, 13);
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Student name is required";
    if (!formData.fatherName.trim())
      newErrors.fatherName = "Father's name is required";

    // Mobile validation (Pakistani format with dash - for frontend UX)
    const mobileRegex = /^03\d{2}-\d{7}$/;
    if (!formData.mobile.match(mobileRegex)) {
      newErrors.mobile = "Mobile must be in format: 0300-1234567";
    }

    // CNIC validation (with dash - for frontend UX)
    const bFormRegex = /^\d{5}-\d{7}-\d{1}$/;
    if (!formData.bForm.match(bFormRegex) && formData.bForm) {
      newErrors.bForm = "CNIC must be in format: 35202-1234567-1";
    }

    if (!formData.address.trim()) newErrors.address = "Address is required";
    if (!formData.gender) newErrors.gender = "Gender is required";
    if (!formData.class) newErrors.class = "Class is required";
    if (!formData.section) newErrors.section = "Section is required";
    if (!formData.studentId)
      newErrors.studentId = "Student ID is required. Please generate it first.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Fetch sections for selected class from PHP backend
  const fetchSections = async (tClass) => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/students.php?action=get-sections-by-class&class=${encodeURIComponent(
          tClass
        )}`
      );
      const fetchedSections = response.data.sections || [];
      setSections(fetchedSections);
      setFormData((prev) => ({ ...prev, section: "" }));
    } catch (error) {
      console.error("Failed to fetch sections:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch sections. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input changes with auto-dash formatting and cursor preservation
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const cursorPos = e.target.selectionStart;

    let formattedValue = value;
    let newCursorPos = cursorPos;

    // Auto-format Mobile and CNIC
    if (name === 'mobile') {
      const raw = value.replace(/-/g, ''); // Remove user-typed dashes to prevent duplication
      formattedValue = formatMobile(raw);
      // Adjust cursor to account for added/removed dashes
      newCursorPos = cursorPos + (formattedValue.length - value.length);
      newCursorPos = Math.max(0, Math.min(formattedValue.length, newCursorPos));
    } else if (name === 'bForm') {
      const raw = value.replace(/-/g, ''); // Remove user-typed dashes
      formattedValue = formatCNIC(raw);
      newCursorPos = cursorPos + (formattedValue.length - value.length);
      newCursorPos = Math.max(0, Math.min(formattedValue.length, newCursorPos));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    // Handle address character count
    if (name === "address") {
      setCharCount(formattedValue.length);
    }

    setFormData((prev) => ({ ...prev, [name]: formattedValue }));

    // Preserve cursor position after DOM updates
    if (name === 'mobile' || name === 'bForm') {
      setTimeout(() => {
        if (e.target === document.activeElement) {
          e.target.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    }

    if (name === "class") {
      fetchSections(value);
    }
  };

  // Handle form submission to PHP backend
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please fix the errors in the form before submitting.",
      });
      return;
    }

    setIsLoading(true);

    try {
      // ✅ FIX: Normalize data for database. Strip dashes 
      // before sending to backend so DB stores numbers only.
      const payload = { ...formData };
      if (payload.mobile) payload.mobile = payload.mobile.replace(/-/g, '');
      if (payload.bForm) payload.bForm = payload.bForm.replace(/-/g, '');

      const response = await axios.post(
        `${API_BASE_URL}/students.php?action=add-solo-student`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      Swal.fire({
        icon: "success",
        title: "Student Registered!",
        text: response.data.message || "Student added successfully",
        showConfirmButton: false,
        timer: 2000,
      });

      // Reset form
      setFormData({
        name: "",
        fatherName: "",
        mobile: "",
        bForm: "",
        address: "",
        gender: "",
        class: "",
        section: "",
        studentId: "",
      });
      setSections([]);
      setCharCount(0);
      setErrors({});
    } catch (error) {
      console.error("Registration error:", error);

      let errorMessage = "An error occurred while registering the student.";

      if (error.response) {
        // The request was made and the server responded with a status code
        if (error.response.status === 400) {
          errorMessage =
            error.response.data.error || "Invalid data. Please check the form.";
        } else if (error.response.status === 409) {
          errorMessage =
            error.response.data.error ||
            "A student with this ID already exists.";
        } else if (error.response.status === 500) {
          errorMessage = "Server error. Please try again later.";
        }
      }

      Swal.fire({
        icon: "error",
        title: "Registration Failed",
        text: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Generate student ID using PHP backend with correct format
  const handleGenerateStudentID = async () => {
    if (!formData.class) {
      Swal.fire({
        icon: "warning",
        title: "Select Class First",
        text: "Please select a class before generating a student ID.",
      });
      return;
    }

    if (!formData.section) {
      Swal.fire({
        icon: "warning",
        title: "Select Section First",
        text: "Please select a section before generating a student ID.",
      });
      return;
    }

    setIsGeneratingId(true);

    const { class: tClass } = formData;
    const year = new Date().getFullYear();

    try {
      // Call PHP backend to get next student ID
      const checkResponse = await axios.get(
        `${API_BASE_URL}/students.php?action=check-student-id&class=${encodeURIComponent(
          tClass
        )}&year=${year}`
      );

      const { lastId, nextId } = checkResponse.data;

      if (nextId) {
        // Use the PHP-generated ID (should be in correct format: Std-PG-25001)
        setFormData((prev) => ({ ...prev, studentId: nextId }));

        Swal.fire({
          icon: "success",
          title: "Student ID Generated",
          html: `
          <div style="text-align: left; font-size: 14px;">
            <p><strong>New ID:</strong> ${nextId}</p>
            ${lastId ? `<p><strong>Previous ID:</strong> ${lastId}</p>` : ""}
            <p><strong>Format:</strong> Std-{ClassPrefix}-{Year}{Sequence}</p>
            <p><strong>Example:</strong> Std-PG-25001 (Play Group, 2025, Student 001)</p>
          </div>
        `,
          showConfirmButton: true,
          confirmButtonText: "OK",
          width: 500,
        });
      } else {
        // Fallback: Generate ID locally if PHP doesn't return one
        const classShortForm = classShortForms[tClass] || "UNK";
        const yearPart = year.toString().slice(-2);

        // Generate sequence - check if we have existing students
        try {
          const existingResponse = await axios.get(
            `${API_BASE_URL}/students.php?action=fetch-students&class=${encodeURIComponent(
              tClass
            )}&year=${year}`
          );

          const existingCount = existingResponse.data.students?.length || 0;
          const nextSequence = existingCount + 1;

          const newId = `Std-${classShortForm}-${yearPart}${nextSequence
            .toString()
            .padStart(3, "0")}`;
          setFormData((prev) => ({ ...prev, studentId: newId }));

          Swal.fire({
            icon: "info",
            title: "Student ID Generated (Local)",
            text: `Generated ID: ${newId}`,
            showConfirmButton: false,
            timer: 1500,
          });
        } catch (fallbackError) {
          // Ultimate fallback
          const newId = `Std-${classShortForm}-${yearPart}001`;
          setFormData((prev) => ({ ...prev, studentId: newId }));

          Swal.fire({
            icon: "info",
            title: "Student ID Generated",
            text: `Using default ID: ${newId}`,
            showConfirmButton: false,
            timer: 1500,
          });
        }
      }
    } catch (error) {
      console.error("Error generating student ID:", error);

      // Ultimate fallback: Generate ID locally
      const classShortForm = classShortForms[formData.class] || "UNK";
      const yearPart = new Date().getFullYear().toString().slice(-2);
      const newId = `Std-${classShortForm}-${yearPart}001`;

      setFormData((prev) => ({ ...prev, studentId: newId }));

      Swal.fire({
        icon: "warning",
        title: "Generated ID (Offline Mode)",
        html: `
        <div style="text-align: left; font-size: 14px;">
          <p><strong>Generated ID:</strong> ${newId}</p>
          <p><strong>Note:</strong> Using offline mode because:</p>
          <ul>
            <li>PHP backend might not be accessible</li>
            <li>Check if backend is running at: ${API_BASE_URL}</li>
            <li>This ID might need manual verification</li>
          </ul>
        </div>
      `,
        showConfirmButton: true,
        width: 500,
      });
    } finally {
      setIsGeneratingId(false);
    }
  };

  // Handle reset form
  const handleReset = () => {
    Swal.fire({
      title: "Clear Form?",
      text: "All entered data will be lost.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#667eea",
      cancelButtonColor: "#718096",
      confirmButtonText: "Yes, clear it!",
      cancelButtonText: "No, keep data",
    }).then((result) => {
      if (result.isConfirmed) {
        setFormData({
          name: "",
          fatherName: "",
          mobile: "",
          bForm: "",
          address: "",
          gender: "",
          class: "",
          section: "",
          studentId: "",
        });
        setSections([]);
        setErrors({});
        setCharCount(0);
        Swal.fire("Cleared!", "Form has been reset.", "success");
      }
    });
  };

  return (
    <div className="solo-dashboard-wrapper">
      {isLoading && (
        <div className="solo-loading-overlay">
          <div className="solo-loader"></div>
          <p style={{ marginTop: "1rem", color: "#4a5568" }}>Processing...</p>
        </div>
      )}

      <Sidebar />

      <div className="solo-main-content">
        <div className="solo-page-header">
          <h1 className="solo-page-title">Register New Student</h1>
          <p className="solo-page-subtitle">
            Fill in all required fields to add a new student profile to the
            system. All fields marked with{" "}
            <span style={{ color: "#e53e3e" }}>*</span> are required.
          </p>
          <div className="solo-api-status">
            <span className="solo-api-status-indicator active"></span>
          </div>
        </div>

        <form className="solo-pro-form" onSubmit={handleSubmit}>
          {/* Personal Information Card */}
          <div className="solo-form-card" data-step="1">
            <div className="solo-card-header">
              <h3>Personal Information</h3>
            </div>
            <div className="solo-form-grid">
              {/* Student Name */}
              <div className="solo-input-group">
                <label className="solo-required-field">Student Name</label>
                <input
                  className={`solo-form-input ${
                    errors.name
                      ? "solo-is-invalid"
                      : formData.name
                      ? "solo-is-valid"
                      : ""
                  }`}
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter full name"
                  maxLength="100"
                  disabled={isLoading}
                />
                {errors.name && (
                  <span className="solo-error-message">{errors.name}</span>
                )}
              </div>

              {/* Father's Name */}
              <div className="solo-input-group">
                <label className="solo-required-field">Father's Name</label>
                <input
                  className={`solo-form-input ${
                    errors.fatherName
                      ? "solo-is-invalid"
                      : formData.fatherName
                      ? "solo-is-valid"
                      : ""
                  }`}
                  type="text"
                  name="fatherName"
                  value={formData.fatherName}
                  onChange={handleInputChange}
                  placeholder="Enter father's full name"
                  maxLength="100"
                  disabled={isLoading}
                />
                {errors.fatherName && (
                  <span className="solo-error-message">
                    {errors.fatherName}
                  </span>
                )}
              </div>

              {/* Mobile Number */}
              <div className="solo-input-group">
                <label className="solo-required-field">Mobile Number</label>
                <input
                  className={`solo-form-input ${
                    errors.mobile
                      ? "solo-is-invalid"
                      : formData.mobile
                      ? "solo-is-valid"
                      : ""
                  }`}
                  type="text"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  placeholder="0300-1234567"
                  pattern="03\d{2}-\d{7}"
                  disabled={isLoading}
                />
                <div className="solo-form-hint">Format: 0300-1234567</div>
                {errors.mobile && (
                  <span className="solo-error-message">{errors.mobile}</span>
                )}
              </div>

              {/* CNIC of Parents */}
              <div className="solo-input-group">
                <label>CNIC of Parents</label>
                <input
                  className={`solo-form-input ${
                    errors.bForm ? "solo-is-invalid" : ""
                  }`}
                  type="text"
                  name="bForm"
                  value={formData.bForm}
                  onChange={handleInputChange}
                  placeholder="35202-1234567-1"
                  pattern="\d{5}-\d{7}-\d{1}"
                  disabled={isLoading}
                />
                <div className="solo-form-hint">
                  Optional: Format 35202-1234567-1
                </div>
                {errors.bForm && (
                  <span className="solo-error-message">{errors.bForm}</span>
                )}
              </div>

              {/* Gender */}
              <div className="solo-input-group">
                <label className="solo-required-field">Gender</label>
                <div className="solo-radio-group">
                  <label className="solo-radio-label">
                    <input
                      type="radio"
                      name="gender"
                      value="Male"
                      checked={formData.gender === "Male"}
                      onChange={handleInputChange}
                      disabled={isLoading}
                    />
                    <span className="solo-radio-custom"></span>
                    Male
                  </label>
                  <label className="solo-radio-label">
                    <input
                      type="radio"
                      name="gender"
                      value="Female"
                      checked={formData.gender === "Female"}
                      onChange={handleInputChange}
                      disabled={isLoading}
                    />
                    <span className="solo-radio-custom"></span>
                    Female
                  </label>
                  <label className="solo-radio-label">
                    <input
                      type="radio"
                      name="gender"
                      value="Other"
                      checked={formData.gender === "Other"}
                      onChange={handleInputChange}
                      disabled={isLoading}
                    />
                    <span className="solo-radio-custom"></span>
                    Other
                  </label>
                </div>
                {errors.gender && (
                  <span className="solo-error-message">{errors.gender}</span>
                )}
              </div>

              {/* Address - Full Width */}
              <div className="solo-input-group full-width">
                <label className="solo-required-field">
                  Residential Address
                </label>
                <div style={{ position: "relative" }}>
                  <textarea
                    className={`solo-form-textarea ${
                      errors.address
                        ? "solo-is-invalid"
                        : formData.address
                        ? "solo-is-valid"
                        : ""
                    }`}
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter complete residential address with city..."
                    rows="4"
                    maxLength="500"
                    disabled={isLoading}
                  />
                  <div
                    className={`solo-char-counter ${
                      charCount > 450 ? "warning" : ""
                    }`}
                  >
                    {charCount}/500 characters
                  </div>
                </div>
                {errors.address && (
                  <span className="solo-error-message">{errors.address}</span>
                )}
              </div>
            </div>
          </div>

          {/* Academic Information Card */}
          <div className="solo-form-card" data-step="2">
            <div className="solo-card-header">
              <h3>Academic Information</h3>
            </div>
            <div className="solo-form-grid">
              {/* Class Selection */}
              <div className="solo-input-group">
                <label className="solo-required-field">Class Allocation</label>
                <select
                  className={`solo-form-select ${
                    errors.class
                      ? "solo-is-invalid"
                      : formData.class
                      ? "solo-is-valid"
                      : ""
                  }`}
                  name="class"
                  value={formData.class}
                  onChange={handleInputChange}
                  disabled={isLoading}
                >
                  <option value="">Select Class</option>
                  {classes.map((cls, index) => (
                    <option key={index} value={cls}>
                      {cls}
                    </option>
                  ))}
                </select>
                {errors.class && (
                  <span className="solo-error-message">{errors.class}</span>
                )}
              </div>

              {/* Section Selection */}
              <div className="solo-input-group">
                <label className="solo-required-field">Section</label>
                <select
                  className={`solo-form-select ${
                    errors.section
                      ? "solo-is-invalid"
                      : formData.section
                      ? "solo-is-valid"
                      : ""
                  }`}
                  name="section"
                  value={formData.section}
                  onChange={handleInputChange}
                  disabled={
                    !formData.class || sections.length === 0 || isLoading
                  }
                >
                  <option value="">
                    {formData.class
                      ? sections.length === 0
                        ? "Loading sections..."
                        : "Select Section"
                      : "Select class first"}
                  </option>
                  {sections.map((sec, index) => (
                    <option key={index} value={sec.section_hint || sec}>
                      {sec.section_name || sec} ({sec.section_hint || sec})
                    </option>
                  ))}
                </select>
                {!formData.class && (
                  <div className="solo-form-hint">
                    Select class to view sections
                  </div>
                )}
                {errors.section && (
                  <span className="solo-error-message">{errors.section}</span>
                )}
              </div>

              {/* Student ID Generator */}
              <div className="solo-id-control-panel">
                <div className="solo-id-actions">
                  <label className="solo-required-field">
                    Student ID Generation
                  </label>
                  <button
                    type="button"
                    className="solo-btn-secondary"
                    onClick={handleGenerateStudentID}
                    disabled={
                      !formData.class ||
                      !formData.section ||
                      isGeneratingId ||
                      isLoading
                    }
                  >
                    {isGeneratingId ? (
                      <>
                        <span className="solo-loader-spinner small"></span>
                        Generating...
                      </>
                    ) : !formData.class || !formData.section ? (
                      "Select Class & Section First"
                    ) : (
                      <>
                        <span>🎯</span> Generate Student ID
                      </>
                    )}
                  </button>
                </div>
                <div className="solo-id-display">
                  <input
                    className={`solo-form-input ${
                      errors.studentId
                        ? "solo-is-invalid"
                        : formData.studentId
                        ? "solo-is-valid"
                        : ""
                    }`}
                    type="text"
                    name="studentId"
                    value={formData.studentId || ""}
                    onChange={handleInputChange}
                    placeholder="ID will appear here after generation"
                    disabled={isLoading}
                  />
                  {errors.studentId && (
                    <span className="solo-error-message">
                      {errors.studentId}
                    </span>
                  )}
                  <div className="solo-field-hint">
                    Auto-generated based on class, section, and sequence
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="solo-form-actions">
            <div className="solo-button-group">
              <button
                type="button"
                className="solo-btn-secondary"
                onClick={handleReset}
                disabled={isLoading}
              >
                <span>🗑️</span> Clear Form
              </button>
              <button
                type="submit"
                className="solo-btn-primary"
                disabled={isLoading || isGeneratingId}
              >
                {isLoading ? (
                  <>
                    <span className="solo-loader-spinner small"></span>
                    Registering...
                  </>
                ) : (
                  <>
                    <span>📝</span> Register Student
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Required Field Note */}
          <div className="solo-required-note">
            Note: All fields marked with an asterisk (*) are required.
          </div>

          {/* Student Preview */}
          {formData.name && formData.class && (
            <div className="solo-student-summary">
              <h4>
                <span>👤</span> Student Preview
              </h4>
              <div className="solo-summary-grid">
                <div className="solo-summary-item">
                  <span className="solo-summary-label">Name:</span>
                  <span className="solo-summary-value">{formData.name}</span>
                </div>
                <div className="solo-summary-item">
                  <span className="solo-summary-label">Father:</span>
                  <span className="solo-summary-value">
                    {formData.fatherName}
                  </span>
                </div>
                <div className="solo-summary-item">
                  <span className="solo-summary-label">Class:</span>
                  <span className="solo-summary-value">
                    {formData.class} - {formData.section || "No Section"}
                  </span>
                </div>
                <div className="solo-summary-item">
                  <span className="solo-summary-label">Gender:</span>
                  <span className="solo-summary-value">
                    {formData.gender || "Not Selected"}
                  </span>
                </div>
                <div className="solo-summary-item">
                  <span className="solo-summary-label">Student ID:</span>
                  <span className="solo-summary-value">
                    {formData.studentId || "Not Generated"}
                  </span>
                </div>
                <div className="solo-summary-item">
                  <span className="solo-summary-label">Status:</span>
                  <span className="solo-status-active">
                    Ready for Registration
                  </span>
                </div>
              </div>
            </div>
          )}
        </form>

        {/* Loading Bar */}
        {isLoading && <div className="solo-loading-bar"></div>}
      </div>
    </div>
  );
};

export default AddStudentForm;